/**
 * StatementOverview.js (Refactored)
 * Main component for displaying and managing uploaded bank statements
 * Now organized with extracted components and hooks for better maintainability
 */

import React from 'react';
import {
  Box,
  Grid,
  Menu,
  MenuItem,
  Alert,
  LinearProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  PlayArrow as StartIcon,
  Visibility as ViewIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';

// Extracted components
import StatementCard from './StatementCard';
import StatementFilters from './StatementFilters';
import StatementDetailsDialog from './StatementDetailsDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

// Custom hooks
import { useStatementFilters } from './useStatementFilters';
import { useStatementActions } from './useStatementActions';

const StatementOverview = ({
  statements,
  isLoading,
  onStatementSelect,
  onStatementDelete,
  selectedStatement,
  onStatementBulkUnreconcile,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Use extracted hooks
  const filters = useStatementFilters(statements);
  const actions = useStatementActions(onStatementSelect, onStatementDelete, onStatementBulkUnreconcile);

  if (isLoading) {
    return (
      <Box sx={{ p: 1 }}>
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  return (
    <>
      {/* Header with Search and Filters */}
      <StatementFilters
        searchTerm={filters.searchTerm}
        filterTab={filters.filterTab}
        sortOrder={filters.sortOrder}
        stats={filters.stats}
        onSearchChange={filters.handleSearchChange}
        onFilterTabChange={filters.handleFilterTabChange}
        onSortToggle={filters.handleSortToggle}
      />

      {/* Statements List */}
      <Box
        sx={{
          flex: 1,
          width: '100%',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: alpha(theme.palette.grey[300], 0.3),
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(theme.palette.grey[500], 0.5),
            borderRadius: '3px',
            '&:hover': {
              background: alpha(theme.palette.grey[600], 0.7),
            },
          },
        }}
      >
        {filters.filteredStatements.length === 0 ? (
          <Alert
            severity="info"
            sx={{
              m: { xs: 2, sm: 3 },
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.info.main,
                0.1
              )} 0%, ${alpha(theme.palette.info.light, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            }}
            icon={<FileIcon sx={{ color: theme.palette.info.main }} />}
          >
            <Typography variant="body2" fontWeight="500">
              {filters.searchTerm || filters.filterTab > 0
                ? 'Nenhum extrato encontrado'
                : 'Nenhum extrato encontrado'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {filters.searchTerm || filters.filterTab > 0
                ? 'Tente ajustar os filtros de busca ou status.'
                : 'Faça upload de um arquivo OFX para começar a conciliação bancária.'}
            </Typography>
          </Alert>
        ) : (
          <Grid container spacing={1} sx={{ p: 0, width: '100%' }}>
            {filters.filteredStatements.map((statement) => (
              <Grid item xs={12} key={statement.id} sx={{ width: '100%', p: 3 }}>
                <StatementCard
                  statement={statement}
                  isSelected={selectedStatement?.id === statement.id}
                  onStatementSelect={onStatementSelect}
                  onMenuOpen={actions.handleMenuOpen}
                  theme={theme}
                  isMobile={isMobile}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={actions.anchorEl}
        open={Boolean(actions.anchorEl)}
        onClose={actions.handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 180 },
        }}
      >
        <MenuItem
          onClick={() => actions.handleStartReconciliation(actions.selectedStatementForAction)}
        >
          <ListItemIcon>
            <StartIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Iniciar Conciliação</ListItemText>
        </MenuItem>
        <MenuItem onClick={actions.handleDetailsClick}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Ver Detalhes</ListItemText>
        </MenuItem>
        {onStatementBulkUnreconcile && (
          <MenuItem onClick={actions.handleBulkUnreconcileClick}>
            <ListItemIcon>
              <LinkOffIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'warning.dark' }}>Desconciliar todo extrato</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={actions.handleDeleteClick} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Excluir</ListItemText>
        </MenuItem>
      </Menu>

      {/* Details Dialog */}
      <StatementDetailsDialog
        open={actions.detailsDialogOpen}
        onClose={actions.handleDetailsClose}
        statement={actions.selectedStatementForAction}
        onStartReconciliation={actions.handleStartFromDetails}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={actions.deleteDialogOpen}
        onClose={actions.handleDeleteCancel}
        onConfirm={actions.handleDeleteConfirm}
        statement={actions.selectedStatementForAction}
      />

      {/* Bulk Unreconcile Confirmation Dialog */}
      <Dialog
        open={actions.bulkUnreconcileDialogOpen}
        onClose={actions.handleBulkUnreconcileClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkOffIcon color="warning" />
          Desconciliar todo extrato
        </DialogTitle>
        <DialogContent>
          <Typography>
            Deseja remover <strong>todas</strong> as conciliações do extrato{' '}
            <em>"{actions.selectedStatementForAction?.fileName}"</em>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Todos os lançamentos conciliados voltar&atilde;o ao status <em>Pendente</em>. Esta
            ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={actions.handleBulkUnreconcileClose}>Cancelar</Button>
          <Button
            onClick={actions.handleBulkUnreconcileConfirm}
            color="warning"
            variant="contained"
          >
            Desconciliar tudo
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StatementOverview;
